import React from 'react';
import AppModal from '../../components/AppModal/AppModal';

/**
 * The confirmation an administrator sees when a story's language is one the
 * destination bucket refuses. Driven entirely by `useExclusionConfirm`, which
 * owns the state and the server round trip.
 *
 * SHORT, on Orr's instruction (2026-11-01): "The prompts can be much shorter.
 * Along the lines of 'It is not possible to place stories with German-language
 * articles in Ippen 2 because language-exclusion applies'." The first draft
 * stated the proportion twice, once in the server's sentence and once in the
 * body, and explained the setting at length. One sentence says it.
 *
 * TWO STEPS when the bucket has not opted in, ONE when it has:
 *   opted in  -> it is not possible, and that is the whole message.
 *   not opted -> the reason, then a separate confirmation that names the
 *                override, because the first dialog in a drag lands mid-gesture
 *                and is dismissed reflexively.
 *
 * Both use the shared <AppModal>, so the navy header, the centred title and the
 * orange primary come from one place. No browser dialog: `window.confirm` is
 * banned repository wide and could not name the bucket anyway.
 */
export default function ExclusionWarningModal({
  verdict, step, onCancel, onFirstContinue, onSecondConfirm,
}) {
  if (!verdict || !step) return null;

  const violation = (verdict.violations || [])[0] || {};
  const bucketName = verdict.bucketName || 'this bucket';
  const languageLabel = violation.label || violation.value || 'that language';
  const enforced = verdict.enforcedOnAllPaths === true;

  if (step === 1) {
    return (
      <AppModal
        isOpen
        title={enforced ? 'Language exclusion applies' : `${bucketName} refuses ${languageLabel}`}
        onCancel={onCancel}
        onConfirm={enforced ? undefined : onFirstContinue}
        confirmLabel="Continue anyway"
        confirmVariant="primary"
        showCancel={!enforced}
        footer={enforced ? (
          // Single action, because there is nothing to decide. Same classes
          // DragIntentModal uses, and the primary stays orange: AppModal cannot
          // style buttons it does not render.
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="pbnew-btn primary" autoFocus onClick={onCancel}>
              Close
            </button>
          </div>
        ) : undefined}
        maxWidth={460}
        modalTestId="exclusion-warning-step-1"
      >
        <p style={{ margin: 0 }}>
          {enforced
            ? `It is not possible to place stories with ${languageLabel}-language articles in `
              + `${bucketName} because language exclusion applies.`
            : `${bucketName} refuses ${languageLabel}, and this story has `
              + `${languageLabel}-language articles.`}
        </p>
      </AppModal>
    );
  }

  return (
    <AppModal
      isOpen
      title={`Add to ${bucketName} anyway?`}
      onCancel={onCancel}
      onConfirm={onSecondConfirm}
      confirmLabel="Add anyway"
      confirmVariant="primary"
      maxWidth={460}
      modalTestId="exclusion-warning-step-2"
    >
      <p style={{ margin: 0 }}>
        This overrides the {languageLabel} exclusion for this story only, and is recorded against
        the suggestion.
      </p>
    </AppModal>
  );
}
